import React from 'react';
import clsx from 'clsx';

/**
 * Button component - pure functional component with Tailwind
 * Supports multiple variants and sizes
 * Supports ref forwarding for keyboard navigation
 */
export const Button = React.forwardRef(({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  className = '',
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary: 'bg-primary text-primary-content hover:opacity-90 focus:ring-primary',
    secondary: 'bg-secondary text-secondary-content hover:opacity-90 focus:ring-secondary',
    ghost: 'text-base-content hover:bg-base-200 focus:ring-neutral',
    danger: 'bg-error text-error-content hover:opacity-90 focus:ring-error',
    success: 'bg-success text-success-content hover:opacity-90 focus:ring-success',
    accent: 'bg-accent text-accent-content hover:opacity-90 focus:ring-accent',
    neutral: 'bg-base-300 text-base-content hover:bg-base-200 focus:ring-neutral'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5'
  };

  const classes = clsx(
    baseClasses,
    variants[variant],
    sizes[size],
    loading && 'cursor-wait',
    className
  );

  const content = (
    <>
      {loading && (
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {icon && iconPosition === 'left' && !loading && (
        <i data-lucide={icon} className="w-4 h-4" />
      )}
      <span>{children}</span>
      {icon && iconPosition === 'right' && !loading && (
        <i data-lucide={icon} className="w-4 h-4" />
      )}
    </>
  );

  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      {...props}
    >
      {content}
    </button>
  );
});

/**
 * IconButton - Button with just an icon using Tailwind
 */
export function IconButton({
  icon,
  onClick,
  size = 'md',
  variant = 'ghost',
  label,
  className = '',
  ...props
}) {
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3'
  };
  
  return (
    <Button
      onClick={onClick}
      variant={variant}
      className={clsx(sizeClasses[size], '!px-0 !py-0 aspect-square', className)}
      aria-label={label}
      {...props}
    >
      <i data-lucide={icon} className={clsx(
        size === 'sm' && 'w-4 h-4',
        size === 'md' && 'w-5 h-5',
        size === 'lg' && 'w-6 h-6'
      )} />
    </Button>
  );
}